<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { RefreshCw, FileBarChart2 } from 'lucide-vue-next'
  import { toast } from 'vue-sonner'
  import configService from '@/services/configService'
  import { formatFileSize } from '@/utils'
  import { InjectionService } from '@/shared/services/InjectionService'
  import {
    AplosFileUploadFileTypes,
    AplosFileUploadService
  } from '../services/AplosFileUploadService'
  import { isValidFileUploadType } from '../services/AplosFileUtilities'

  const emit = defineEmits<{
    beforeUpload: [success: boolean]
    afterUpload: [success: boolean]
  }>()

  const file = ref<File | null>(null)
  const progress = ref(0)
  const uploading = ref(false)
  const uploadingIsDone = ref(false)
  const hasUploadingError = ref(false)
  const maxFileSize = ref(0)

  const fileUploadService: AplosFileUploadService = InjectionService.injectStrict(
    AplosFileUploadService.injectableKey
  )

  onMounted(async () => {
    maxFileSize.value = await configService.getMaxFileSize()
  })
  // return true if the file matches our allowed types
  function isUploadFileValid(targetFile: File) {
    const ext = '.' + targetFile.name.split('.').pop()?.toLowerCase()
    return isValidFileUploadType(targetFile.type) || isValidFileUploadType(ext)
  }

  // centralised upload starter used by both click selection and drop
  async function startUpload(uploadedFile: File) {
    file.value = uploadedFile
    uploadingIsDone.value = false
    hasUploadingError.value = false

    emit('beforeUpload', true)
    try {
      await fileUploadService.handleUpload(uploadedFile, uploading, progress, handleStatusUpdate)
    } catch (err) {
      hasUploadingError.value = true
      throw err
    } finally {
      emit('afterUpload', hasUploadingError.value)
    }
  }

  // used by the Ant Upload `before-upload` handler (must return false to prevent default)
  async function beforeUpload(uploadedFile: File) {
    if (!isUploadFileValid(uploadedFile)) {
      toast.error('Invalid file type: ' + uploadedFile.name)
      return false
    }

    try {
      await startUpload(uploadedFile)
    } catch {
      // startUpload sets hasUploadingError and rethrows; swallow here
    }

    return false
  }

  async function onDrop(e: DragEvent) {
    const dt = e.dataTransfer
    if (!dt) return
    const files = Array.from(dt.files || [])
    if (!files.length) return

    if (uploading.value) {
      //toast.error('Upload already in progress')
      return
    }

    for (const f of files) {
      if (!isUploadFileValid(f)) {
        toast.error('Invalid file type: ' + f.name)
        continue
      }

      try {
        await startUpload(f)
      } catch {
        // error state handled in startUpload
      }
    }
  }

  function handleStatusUpdate(hasError: boolean) {
    uploadingIsDone.value = true
    hasUploadingError.value = hasError
  }
</script>

<template>
  <div class="grid place-content-end gap-1">
    <a-upload-dragger
      v-if="!uploading"
      data-testid="file-dropzone"
      name="file"
      :multiple="true"
      :showUploadList="false"
      @dragover.prevent
      @drop.prevent="onDrop"
      :before-upload="beforeUpload"
      :accept="Object.values(AplosFileUploadFileTypes).join(', ')"
    >
      <div class="grid place-content-center gap-2 px-4 py-2">
        <FileBarChart2 class="mx-auto size-5 text-gray-300 dark:text-gray-500" aria-hidden="true" />
        <p class="text-sm">Click or Drag & Drop</p>
        <p v-if="maxFileSize" class="text-xs text-gray-500 dark:text-gray-400">
          Max: {{ formatFileSize(maxFileSize) }}
        </p>
        <p class="text-[0.625rem] text-gray-500 dark:text-gray-400">
          For larger files please use the API
        </p>
      </div>
    </a-upload-dragger>
    <div
      v-if="uploading"
      class="grid h-24 w-40 place-content-center gap-2 rounded-md border border-dashed px-4 py-2"
    >
      <RefreshCw
        class="mx-auto size-5 animate-spin text-gray-300 dark:text-gray-500"
        aria-hidden="true"
      />
      <p class="text-xs font-medium leading-5 text-gray-600 dark:text-gray-300">
        {{ uploadingIsDone ? 'Processing...' : 'Uploading...' }}
      </p>
    </div>
    <a-progress v-if="uploading" class="!m-0" :percent="progress" />
  </div>
</template>
